import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { pipelineKeys } from '@/lib/query-keys';
import { pipelineService } from '@/services/pipeline.service';

export function usePipelines() {
  const queryClient = useQueryClient();

  const pipelinesQuery = useQuery({
    queryKey: pipelineKeys.list(),
    queryFn: () => pipelineService.getAll(),
  });

  const createPipeline = useMutation({
    mutationFn: (data: Parameters<typeof pipelineService.create>[0]) => pipelineService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.all });
      toast.success('Pipeline created successfully');
    },
    onError: () => toast.error('Failed to create pipeline'),
  });

  const updatePipeline = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof pipelineService.update>[1] }) =>
      pipelineService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.all });
      toast.success('Pipeline updated successfully');
    },
    onError: () => toast.error('Failed to update pipeline'),
  });

  const deletePipeline = useMutation({
    mutationFn: (id: string) => pipelineService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.all });
      toast.success('Pipeline deleted successfully');
    },
    onError: () => toast.error('Failed to delete pipeline'),
  });

  const addStage = useMutation({
    mutationFn: ({ pipelineId, data }: { pipelineId: string; data: Parameters<typeof pipelineService.addStage>[1] }) =>
      pipelineService.addStage(pipelineId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.all });
      toast.success('Stage added successfully');
    },
    onError: () => toast.error('Failed to add stage'),
  });

  const updateStage = useMutation({
    mutationFn: ({ pipelineId, stageId, data }: { pipelineId: string; stageId: string; data: Parameters<typeof pipelineService.updateStage>[2] }) =>
      pipelineService.updateStage(pipelineId, stageId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.all });
      toast.success('Stage updated successfully');
    },
    onError: () => toast.error('Failed to update stage'),
  });

  const deleteStage = useMutation({
    mutationFn: ({ pipelineId, stageId }: { pipelineId: string; stageId: string }) =>
      pipelineService.deleteStage(pipelineId, stageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.all });
      toast.success('Stage deleted successfully');
    },
    onError: () => toast.error('Failed to delete stage'),
  });

  const reorderStages = useMutation({
    mutationFn: ({ pipelineId, stageIds }: { pipelineId: string; stageIds: string[] }) =>
      pipelineService.reorderStages(pipelineId, stageIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pipelineKeys.all });
    },
    onError: () => toast.error('Failed to reorder stages'),
  });

  return {
    pipelines: pipelinesQuery.data?.data ?? [],
    isLoading: pipelinesQuery.isLoading,
    createPipeline,
    updatePipeline,
    deletePipeline,
    addStage,
    updateStage,
    deleteStage,
    reorderStages,
  };
}
